// Copyright (c) 2025 Goffy. All Rights Reserved.

import {
  ActivityIndicator,
  Modal,
  Pressable,
  View as RNView,
  type ImageSourcePropType,
  Image,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { formatHoleDistance } from '@/features/units/distance';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { PositionByHoleGraph, type PositionByHoleSeriesEntry } from '@/components/PositionByHoleGraph';
import { ShareLinkButton } from '@/components/ShareLinkButton';
import { t } from '@/i18n';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { getGameDisplayName, getSubgameDisplayName } from '@/features/game-definitions/i18n';
import { groupItems, normalizeGameKey } from '@/features/game-definitions/groups';
import { ECLECTIC_GAME_ID, FREEZE_GAME_ID } from '@/features/game-definitions/tournamentOnly';
import { compareHandicaps, resolveTieBreakHandicap } from '@/features/leaderboard/tieBreak';
import FreezeStandingsCard from '@/features/tour-leaderboard/components/FreezeStandingsCard';
import EclecticStandingsCard from '@/features/tour-leaderboard/components/EclecticStandingsCard';
import { normalizeEclecticSection, normalizeFreezeSection } from '@/features/tour-games/normalize';
import { buildEclecticSectionThroughRound } from '@/features/tour-games/eclectic';
import { buildFreezeSectionThroughRound } from '@/features/tour-games/freeze';
import type { EclecticSection, FreezeSection } from '@/features/tour-games/types';
import { buildAccessHeaders, appendClaimQuery, hasClaimAccess } from '@/services/claimHttp';
import { useInviteViewer } from '@/hooks/useInviteViewer';
import { getActiveClaimSessionSync, loadClaimSession, subscribeClaimSession } from '@/services/claimSession';
import { setCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';
import {
  BestBallSelectImage,
  BunkerSelectImage,
  HighLowSelectImage,
  MysterySelectImage,
  PenaltyIcon,
  SkinsSelectImage,
  StablefordSelectImage,
  ThreePuttIcon,
  TrophyOnlyImage,
  WaterSelectImage,
} from '@/features/hole/assets';

// handicap is the HCP index and shcp the round's playing handicap; both come from
// /api/rounds/{roundId}/players and break players level on merits.
type Player = {
  id: string;
  name: string;
  handicap?: number | string | null;
  Handicap?: number | string | null;
  shcp?: number | string | null;
  scp?: number | string | null;
};
type GameScore = {
  gameId?: string;
  GameId?: string;
  gameName?: string;
  GameName?: string;
  playerId?: string;
  PlayerId?: string;
  scorePoints?: number;
  ScorePoints?: number;
  gamePoints?: number;
  GamePoints?: number;
  roundGamePoints?: number;
  RoundGamePoints?: number;
};
type PlayerTotalGamePoints = {
  gameId?: string;
  GameId?: string;
  roundGamePoints?: number;
  RoundGamePoints?: number;
};
type PlayerTotal = {
  playerId?: string;
  PlayerId?: string;
  totalRoundGamePoints?: number;
  TotalRoundGamePoints?: number;
  roundGamePoints?: PlayerTotalGamePoints[];
  RoundGamePoints?: PlayerTotalGamePoints[];
};
type RoundScoreboardResponse = {
  roundId?: string;
  RoundId?: string;
  scores?: GameScore[];
  Scores?: GameScore[];
  totals?: PlayerTotal[];
  Totals?: PlayerTotal[];
};
type HoleEntry = {
  playerId?: string;
  PlayerId?: string;
  hole?: number;
  Hole?: number;
  holeNumber?: number;
  HoleNumber?: number;
  par?: number;
  Par?: number;
  grossStroke?: number | null;
  GrossStroke?: number | null;
  grossStrokes?: number | null;
  GrossStrokes?: number | null;
  pickup?: boolean;
  Pickup?: boolean;
};
type RoundSubgameEntry = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  subgameId?: string;
  SubgameId?: string;
};
type HoleEventEntry = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  eventType?: string;
  EventType?: string;
  eventValue?: string | null;
  EventValue?: string | null;
};
type GameDefinition = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
  resultUnit?: string;
  ResultUnit?: string;
};
type MeritsByHoleResponse = {
  roundId?: string;
  RoundId?: string;
  holes?: number[];
  Holes?: number[];
  series?: Array<{ playerId?: string; PlayerId?: string; totals?: number[]; Totals?: number[] }>;
  Series?: Array<{ playerId?: string; PlayerId?: string; totals?: number[]; Totals?: number[] }>;
};
type Round = {
  id: string;
  tourId?: string | null;
  name?: string;
  courseName?: string;
  status?: string;
  createdAt: string;
  players: string[];
};

type GameResultRow = {
  playerId: string;
  playerName: string;
  points: number;
  merits: number;
};

type GameResultCard = {
  gameId: string;
  gameName: string;
  unit: string;
  rows: GameResultRow[];
};

type MysteryHighlight = {
  holeNumber: number;
  mysteryPrize: string;
  subgameName: string;
  winnerName: string;
};

type ParOrBetterRow = {
  playerId: string;
  playerName: string;
  resultGroups: Array<{
    resultLabel: string;
    resultType: 'par' | 'birdie' | 'eagle';
    holeNumbers: number[];
  }>;
};

type HazardByPlayerRow = {
  playerId: string;
  playerName: string;
  total: number;
  water: number;
  bunker: number;
  threePutt: number;
  penalty: number;
};

/** One player's automatically tracked hole, from GET /api/rounds/{id}/shots. */
type TrackedShotHole = {
  playerId: string;
  holeNumber: number;
  putts: number;
  shots: Array<{ shotNumber: number; distanceMeters: number | null }>;
};

const GAME_ICON_MAP: Record<string, ImageSourcePropType> = {
  'best-ball': BestBallSelectImage,
  bunker: BunkerSelectImage,
  'high-low': HighLowSelectImage,
  freeze: MysterySelectImage,
  mystery: MysterySelectImage,
  skins: SkinsSelectImage,
  stableford: StablefordSelectImage,
  'stroke-play': StablefordSelectImage,
  water: WaterSelectImage,
};

const isStrokePlayGame = (gameId: string | undefined, gameName: string | undefined): boolean => {
  return normalizeGameKey(gameId) === 'stroke-play' || normalizeGameKey(gameName) === 'stroke-play';
};

type HazardType = 'water' | 'bunker' | 'threePutt' | 'penalty';

const HAZARD_ICON_MAP: Record<HazardType, ImageSourcePropType> = {
  water: WaterSelectImage,
  bunker: BunkerSelectImage,
  threePutt: ThreePuttIcon,
  penalty: PenaltyIcon,
};

const normalizeHazardType = (value: string): HazardType | null => {
  const normalized = value.replace(/[\s_-]+/g, '').toLowerCase();
  if (normalized === 'water') return 'water';
  if (normalized === 'bunker') return 'bunker';
  if (normalized === 'threeputt') return 'threePutt';
  if (normalized === 'penalty') return 'penalty';
  return null;
};

const getString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
};

const getNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return undefined;
};

const getBoolean = (...values: Array<unknown>): boolean | undefined => {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return undefined;
};

const parseEventValueObject = (value: string | null | undefined): Record<string, unknown> | null => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
};

const getParResult = (
  gross: number,
  par: number
): { resultLabel: string; resultType: 'par' | 'birdie' | 'eagle' | 'albatross' | 'underPar' } => {
  const delta = gross - par;
  if (delta === 0) return { resultLabel: t('summary.resultPar'), resultType: 'par' };
  if (delta === -1) return { resultLabel: t('summary.resultBirdie'), resultType: 'birdie' };
  if (delta === -2) return { resultLabel: t('summary.resultEagle'), resultType: 'eagle' };
  if (delta <= -3) return { resultLabel: t('summary.resultAlbatross'), resultType: 'albatross' };
  return { resultLabel: t('summary.resultUnderPar'), resultType: 'underPar' };
};

const isRoundSelectableByStatus = (status: string | undefined): boolean => {
  const normalized = String(status ?? '').trim().toLowerCase();
  return (
    normalized === 'open' ||
    normalized === 'active' ||
    normalized === 'playing' ||
    normalized === 'started' ||
    normalized === 'finished'
  );
};

export default function RoundSummaryScreen() {
  const { roundId: roundIdParam, tourId: tourIdParam } = useLocalSearchParams<{ roundId?: string; tourId?: string }>();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const router = useRouter();
  const storedRoundId = useRoundState((state) => state.roundId);
  const storedHole = useRoundState((state) => state.hole);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setRoundStatus = useRoundState((state) => state.setRoundStatus);
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const [round, setRound] = useState<Round | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [trackedShotHoles, setTrackedShotHoles] = useState<TrackedShotHole[]>([]);
  const distanceUnit = useDistanceUnit();
  const [scores, setScores] = useState<GameScore[]>([]);
  const [playerTotals, setPlayerTotals] = useState<PlayerTotal[]>([]);
  const [holeEntries, setHoleEntries] = useState<HoleEntry[]>([]);
  const [roundSubgames, setRoundSubgames] = useState<RoundSubgameEntry[]>([]);
  const [holeEvents, setHoleEvents] = useState<HoleEventEntry[]>([]);
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [meritsByHole, setMeritsByHole] = useState<MeritsByHoleResponse | null>(null);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [tourHasFreeze, setTourHasFreeze] = useState(false);
  const [tourHasEclectic, setTourHasEclectic] = useState(false);
  const [freezeSection, setFreezeSection] = useState<FreezeSection | null>(null);
  const [eclecticSection, setEclecticSection] = useState<EclecticSection | null>(null);
  const [roundsError, setRoundsError] = useState<string | null>(null);
  const [roundPickerOpen, setRoundPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reopening, setReopening] = useState(false);
  const [reopenError, setReopenError] = useState<string | null>(null);
  const effectiveTourId = useMemo(() => {
    const routeTourId = Array.isArray(tourIdParam) ? tourIdParam[0] : tourIdParam;
    // Fall back to the round's own tour id (from the round detail payload) so the
    // share button works on the end-of-round summary, which is opened without a
    // ?tourId= param and outside any guest claim session.
    const roundTourId =
      (round as { tourId?: string | null; TourId?: string | null } | null)?.tourId ??
      (round as { TourId?: string | null } | null)?.TourId ??
      null;
    return routeTourId ?? claimSession?.tourId ?? roundTourId ?? null;
  }, [claimSession?.tourId, tourIdParam, round]);

  // Keep the globally-remembered tour in sync with this round's tour so the bottom
  // menu's Tour button stays enabled and points at the right tour. Without this the
  // menu can hold a stale (often hidden single-round) tour id and disable the button.
  useEffect(() => {
    if (effectiveTourId) {
      setCurrentTourId(effectiveTourId, { preserveRound: true });
    }
  }, [effectiveTourId]);
  const hasGuestClaimSessionAccess = !!claimSession?.claimClientId && (hasClaimAccess(claimSession) || !!claimSession?.inviteToken?.trim());
  const isGuestClaimMode = !user?.token && hasGuestClaimSessionAccess;
  // Invite viewers are a second, distinct kind of anonymous visitor: no claim session, no player,
  // just an invite token in the URL. Kept separate from isGuestClaimMode on purpose — a viewer must
  // not get the claim-guest affordances (and vice versa). The REST layer already authorizes
  // ?inviteToken= on every round read, so the credentials just need to reach appendClaimQuery.
  const { isInviteViewer, accessSession: viewerAccessSession, viewerRouteParams } = useInviteViewer();
  const anonymousReadAllowed = isGuestClaimMode || isInviteViewer;
  const accessSession = claimSession ?? viewerAccessSession;
  // Note this is stricter than HoleScreen's canWrite, which admits claim guests: the two
  // affordances it gates here (reopen, freeze lock) are dead ends for a claim guest anyway —
  // handleReopenRound no-ops without a token, and /round/{id}/freeze redirects them to "/".
  // Spectating overrides a signed-in token, same as everywhere else.
  const canWrite = !isInviteViewer && !!user?.token;
  const effectiveRoundId = useMemo(() => {
    const routeRoundId = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return routeRoundId ?? claimSession?.startedRoundId ?? storedRoundId ?? null;
  }, [claimSession?.startedRoundId, roundIdParam, storedRoundId]);

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });
    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
      setClaimSessionLoaded(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const fetchRoundData = useCallback(async () => {
    if (!effectiveRoundId || (!user?.token && !anonymousReadAllowed)) {
      setLoading(false);
      setError(t('summary.missingInfo'));
      return;
    }

    try {
      setLoading(true);
      const headers = buildAccessHeaders(user?.token);
      const [roundRes, playersRes, scoreboardRes] = await Promise.all([
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`, accessSession), {
          headers,
        }),
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/players`, accessSession), {
          headers,
        }),
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/scoreboard`, accessSession), {
          headers,
        }),
      ]);

      if (!roundRes.ok || !playersRes.ok || !scoreboardRes.ok) {
        throw new Error(t('summary.loadError'));
      }

      const fetchOptionalJson = async <T,>(
        label: string,
        url: string,
        fallback: T,
      ): Promise<T> => {
        try {
          const response = await fetch(url, { headers });
          if (!response.ok) {
            console.warn('[round-summary] optional fetch failed', {
              label,
              url,
              status: response.status,
            });
            return fallback;
          }

          return (await response.json()) as T;
        } catch (error) {
          console.warn('[round-summary] optional fetch rejected', {
            label,
            url,
            message: error instanceof Error ? error.message : String(error),
          });
          return fallback;
        }
      };

      const [roundData, playersData, scoreboardData, holeEntriesData, subgamesData, holeEventsData, gameDefsData, meritsByHoleData] =
        await Promise.all([
          roundRes.json(),
          playersRes.json(),
          scoreboardRes.json() as Promise<RoundScoreboardResponse>,
          fetchOptionalJson(
            'hole-entries',
            appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-entries`, accessSession),
            [] as unknown[],
          ),
          fetchOptionalJson(
            'subgames',
            appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/subgames`, accessSession),
            [] as unknown[],
          ),
          fetchOptionalJson(
            'hole-events',
            appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-events`, accessSession),
            [] as unknown[],
          ),
          fetchOptionalJson(
            'game-definitions',
            appendClaimQuery(`${apiBaseUrl}/api/game-definitions`, accessSession),
            [] as unknown[],
          ),
          fetchOptionalJson<MeritsByHoleResponse | null>(
            'merits-by-hole',
            appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/merits-by-hole`, accessSession),
            null,
          ),
        ]);

      const normalizedPlayers = (Array.isArray(playersData) ? playersData : (playersData.players ?? playersData.Players ?? []));
      const normalizedScores = Array.isArray(scoreboardData.scores)
        ? scoreboardData.scores
        : Array.isArray(scoreboardData.Scores)
          ? scoreboardData.Scores
          : [];
      const normalizedTotals = Array.isArray(scoreboardData.totals)
        ? scoreboardData.totals
        : Array.isArray(scoreboardData.Totals)
          ? scoreboardData.Totals
          : [];

      setRound(roundData);
      setPlayers(normalizedPlayers);
      setScores(normalizedScores);
      setPlayerTotals(normalizedTotals);
      setHoleEntries(Array.isArray(holeEntriesData) ? holeEntriesData : []);
      setRoundSubgames(Array.isArray(subgamesData) ? subgamesData : []);
      setHoleEvents(Array.isArray(holeEventsData) ? holeEventsData : []);
      setGameDefinitions(Array.isArray(gameDefsData) ? gameDefsData : []);
      setMeritsByHole(meritsByHoleData && typeof meritsByHoleData === 'object' ? meritsByHoleData : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('summary.loadError'));
    } finally {
      setLoading(false);
    }
  }, [accessSession, apiBaseUrl, effectiveRoundId, anonymousReadAllowed, user?.token]);

  useEffect(() => {
    if (!user?.token && !isInviteViewer && !claimSessionLoaded) {
      return;
    }
    fetchRoundData();
  }, [claimSessionLoaded, fetchRoundData, isInviteViewer, user?.token]);

  useEffect(() => {
    if (!effectiveRoundId) {
      setRoundId(null);
      setRoundStatus(null);
      return;
    }

    setRoundId(effectiveRoundId);
  }, [effectiveRoundId, setRoundId, setRoundStatus]);

  useEffect(() => {
    const matchingRound = rounds.find((candidate) => candidate.id === effectiveRoundId) ?? null;
    setRoundStatus(round?.status ?? matchingRound?.status ?? null);
  }, [effectiveRoundId, round?.status, rounds, setRoundStatus]);

  useEffect(() => {
    if (!effectiveTourId || (!user?.token && !anonymousReadAllowed)) {
      setRounds([]);
      setRoundsError(null);
      setRoundsLoading(false);
      return;
    }

    let cancelled = false;
    const loadRounds = async () => {
      setRoundsLoading(true);
      setRoundsError(null);
      try {
        // Include finished rounds: the endpoint defaults to {draft,active,playing} and would
        // otherwise hide completed rounds from the round picker, even though their summaries
        // are exactly what a player wants to browse here.
        const response = await fetch(
          appendClaimQuery(
            `${apiBaseUrl}/api/rounds/tour/${effectiveTourId}?status=draft,open,active,playing,started,finished`,
            accessSession,
          ),
          {
            headers: buildAccessHeaders(user?.token),
          },
        );
        if (!response.ok) {
          throw new Error(`${t('scorecard.selectRoundError')} (${response.status})`);
        }

        const payload = (await response.json()) as Round[];
        if (!cancelled) {
          setRounds(payload);
        }
      } catch {
        if (!cancelled) {
          setRoundsError(t('scorecard.selectRoundError'));
        }
      } finally {
        if (!cancelled) {
          setRoundsLoading(false);
        }
      }
    };

    loadRounds();

    return () => {
      cancelled = true;
    };
  }, [accessSession, apiBaseUrl, effectiveTourId, anonymousReadAllowed, user?.token]);

  // Freeze and Eclectic are tour-long games: Freeze lets players lock in
  // Stableford points on chosen holes after a finished round; Eclectic tracks
  // the best points per hole across the tour. Show their round-summary cards
  // when the tour has them among its tournament games.
  useEffect(() => {
    if (!effectiveTourId || (!user?.token && !anonymousReadAllowed)) {
      setTourHasFreeze(false);
      setTourHasEclectic(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          appendClaimQuery(`${apiBaseUrl}/api/tours/${effectiveTourId}`, accessSession),
          { headers: buildAccessHeaders(user?.token) },
        );
        if (!response.ok) return;
        const data = (await response.json()) as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
        };
        const gameIds = data.gameDefinitionIds ?? data.GameDefinitionIds ?? [];
        if (!cancelled) {
          setTourHasFreeze(gameIds.some((gameId) => gameId?.toLowerCase() === FREEZE_GAME_ID));
          setTourHasEclectic(gameIds.some((gameId) => gameId?.toLowerCase() === ECLECTIC_GAME_ID));
        }
      } catch {
        if (!cancelled) {
          setTourHasFreeze(false);
          setTourHasEclectic(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessSession, apiBaseUrl, effectiveTourId, anonymousReadAllowed, user?.token]);

  // Freeze/Eclectic standings ride the same tour.leaderboard.state/update
  // stream the tour leaderboard screen uses — only the freeze/eclectic
  // sections of that payload are consumed here.
  useEffect(() => {
    if (!effectiveTourId || !(tourHasFreeze || tourHasEclectic)) {
      setFreezeSection(null);
      setEclecticSection(null);
      return;
    }

    const socket = getSharedBffWebSocket(apiBaseUrl);
    const unsubscribe = socket.onMessage((message) => {
      if (message.type !== 'tour.leaderboard.state' && message.type !== 'tour.leaderboard.update') {
        return;
      }
      const event = message as { tourId?: string; eclectic?: unknown; freeze?: unknown };
      if (String(event.tourId ?? '').trim().toLowerCase() !== effectiveTourId.trim().toLowerCase()) {
        return;
      }
      setFreezeSection(normalizeFreezeSection(event.freeze));
      setEclecticSection(normalizeEclecticSection(event.eclectic));
    });

    socket.sendTourLeaderboardRequest({
      tourId: effectiveTourId,
      inviteToken: viewerAccessSession?.inviteToken ?? undefined,
    });

    return () => {
      unsubscribe();
    };
  }, [apiBaseUrl, effectiveTourId, tourHasFreeze, tourHasEclectic, viewerAccessSession?.inviteToken]);

  // Automatically tracked strokes and putt counts — the durable read of shot
  // tracking, so the summary shows them long after the live socket is gone.
  // Silent on failure: telemetry decorates the summary, it never blocks it.
  useEffect(() => {
    if (!effectiveRoundId || (!user?.token && !isGuestClaimMode)) {
      setTrackedShotHoles([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/shots`, claimSession),
          { headers: buildAccessHeaders(user?.token) },
        );
        if (!response.ok) return;
        const data = (await response.json()) as { holes?: unknown };
        const holes = Array.isArray(data.holes) ? data.holes : [];
        const parsed: TrackedShotHole[] = holes
          .map((hole) => {
            if (!hole || typeof hole !== 'object') return null;
            const record = hole as Record<string, unknown>;
            if (typeof record.playerId !== 'string' || typeof record.holeNumber !== 'number') {
              return null;
            }
            const shots = Array.isArray(record.shots) ? record.shots : [];
            return {
              playerId: record.playerId,
              holeNumber: record.holeNumber,
              putts: typeof record.putts === 'number' ? record.putts : 0,
              shots: shots
                .map((shot) => {
                  if (!shot || typeof shot !== 'object') return null;
                  const shotRecord = shot as Record<string, unknown>;
                  if (typeof shotRecord.shotNumber !== 'number') return null;
                  return {
                    shotNumber: shotRecord.shotNumber,
                    distanceMeters:
                      typeof shotRecord.distanceMeters === 'number'
                        ? shotRecord.distanceMeters
                        : null,
                  };
                })
                .filter((shot): shot is TrackedShotHole['shots'][number] => shot !== null),
            };
          })
          .filter((hole): hole is TrackedShotHole => hole !== null);
        if (!cancelled) setTrackedShotHoles(parsed);
      } catch {
        if (!cancelled) setTrackedShotHoles([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, claimSession, effectiveRoundId, isGuestClaimMode, user?.token]);

  const trackedShotsByPlayer = useMemo(() => {
    const groups = new Map<string, TrackedShotHole[]>();
    for (const hole of trackedShotHoles) {
      if (hole.shots.length === 0 && hole.putts === 0) continue;
      const list = groups.get(hole.playerId) ?? [];
      list.push(hole);
      groups.set(hole.playerId, list);
    }
    return [...groups.entries()]
      .map(([playerId, holes]) => ({
        playerId,
        holes: [...holes].sort((a, b) => a.holeNumber - b.holeNumber),
      }))
      .sort((a, b) => a.playerId.localeCompare(b.playerId));
  }, [trackedShotHoles]);

  const playerById = useMemo(() => {
    const map = new Map<string, Player>();
    players.forEach((player) => map.set(player.id, player));
    return map;
  }, [players]);

  const gameDefinitionNameById = useMemo(() => {
    const map = new Map<string, string>();
    gameDefinitions.forEach((definition) => {
      const id = getString(definition.id, definition.Id);
      const name = getGameDisplayName(
        {
          id,
          name: getString(definition.name, definition.Name),
          info: getString(definition.info, definition.Info),
          description: getString(definition.description, definition.Description),
        },
        t('summary.unknownGame')
      );
      if (id && name) {
        map.set(id.toLowerCase(), name);
      }
    });
    return map;
  }, [gameDefinitions]);

  const gameDefinitionUnitById = useMemo(() => {
    const map = new Map<string, string>();
    gameDefinitions.forEach((definition) => {
      const id = getString(definition.id, definition.Id);
      const unit = getString(definition.resultUnit, definition.ResultUnit);
      if (id && unit) {
        map.set(id.toLowerCase(), unit);
      }
    });
    return map;
  }, [gameDefinitions]);

  const freezeEclecticPlayerNameLookup = useMemo(
    () => new Map(players.map((player) => [player.id, player.name])),
    [players]
  );

  // The backend sections are always the tour's current tally; scope them to
  // the round being viewed so browsing past rounds shows that round's result
  // (e.g. round 2 of 3) instead of always the latest/live one.
  const scopedEclecticSection = useMemo(
    () => buildEclecticSectionThroughRound(eclecticSection, effectiveRoundId),
    [eclecticSection, effectiveRoundId]
  );
  const scopedFreezeSection = useMemo(
    () => buildFreezeSectionThroughRound(freezeSection, effectiveRoundId),
    [freezeSection, effectiveRoundId]
  );

  const playerScores = useMemo(() => {
    const scoresByPlayer: Record<string, { total: number; games: Record<string, number> }> = {};
    players.forEach((player) => {
      scoresByPlayer[player.id] = { total: 0, games: {} };
    });

    scores.forEach((score) => {
      const playerId = getString(score.playerId, score.PlayerId);
      const gameName = getString(score.gameName, score.GameName);
      const points = getNumber(score.scorePoints, score.ScorePoints) ?? 0;
      if (playerId && scoresByPlayer[playerId]) {
        scoresByPlayer[playerId].total += points;
        if (gameName) {
          scoresByPlayer[playerId].games[gameName] = points;
        }
      }
    });

    // Ranking/placement based game points per player for this round.
    const gamePointsByPlayer: Record<string, number> = {};
    playerTotals.forEach((total) => {
      const playerId = getString(total.playerId, total.PlayerId);
      if (playerId) {
        gamePointsByPlayer[playerId] =
          getNumber(total.totalRoundGamePoints, total.TotalRoundGamePoints) ?? 0;
      }
    });

    return players
      .map((player) => ({
        ...player,
        scorePoints: scoresByPlayer[player.id]?.total ?? 0,
        gamePoints: gamePointsByPlayer[player.id] ?? 0,
        games: scoresByPlayer[player.id]?.games ?? {},
        // Shared merits go to the lower handicap: HCP index when known, else the round SHCP.
        tieBreakHandicap: resolveTieBreakHandicap(
          player.handicap ?? player.Handicap,
          player.shcp ?? player.scp
        ),
      }))
      .sort(
        (a, b) =>
          b.gamePoints - a.gamePoints ||
          compareHandicaps(a.tieBreakHandicap, b.tieBreakHandicap) ||
          b.scorePoints - a.scorePoints
      );
  }, [players, scores, playerTotals]);

  const gameResultCards = useMemo(() => {
    // Per-player, per-game merit points from the round leaderboard breakdown.
    const meritsByPlayerGame = new Map<string, number>();
    playerTotals.forEach((total) => {
      const playerId = getString(total.playerId, total.PlayerId);
      if (!playerId) return;
      const breakdown = total.roundGamePoints ?? total.RoundGamePoints ?? [];
      breakdown.forEach((entry) => {
        const gameId = getString(entry.gameId, entry.GameId);
        const merits = getNumber(entry.roundGamePoints, entry.RoundGamePoints);
        if (gameId && merits !== undefined && merits !== null) {
          meritsByPlayerGame.set(`${playerId}:${gameId.toLowerCase()}`, merits);
        }
      });
    });

    const byGame = new Map<string, { gameId: string; gameName: string; playerPoints: Map<string, number> }>();
    scores.forEach((score) => {
      const rawGameId = getString(score.gameId, score.GameId) ?? '';
      const gameName =
        getString(
          score.gameName,
          score.GameName,
          rawGameId ? gameDefinitionNameById.get(rawGameId.toLowerCase()) : undefined
        ) ?? t('summary.unknownGame');
      const key = rawGameId || gameName;
      if (!key) return;
      if (!byGame.has(key)) {
        byGame.set(key, {
          gameId: rawGameId || key,
          gameName,
          playerPoints: new Map<string, number>(),
        });
      }
      const entry = byGame.get(key);
      if (!entry) return;
      if (entry.gameName === t('summary.unknownGame') && gameName !== t('summary.unknownGame')) {
        entry.gameName = gameName;
      }
      const playerId = getString(score.playerId, score.PlayerId);
      const points = getNumber(score.scorePoints, score.ScorePoints) ?? 0;
      if (!playerId) return;
      entry.playerPoints.set(playerId, (entry.playerPoints.get(playerId) ?? 0) + points);
    });

    return Array.from(byGame.values())
      .map((game): GameResultCard => {
        const rows = Array.from(game.playerPoints.entries())
          .map(([playerId, points]) => ({
            playerId,
            playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
            points,
            merits: meritsByPlayerGame.get(`${playerId}:${game.gameId.toLowerCase()}`) ?? 0,
          }))
          .sort((a, b) =>
            isStrokePlayGame(game.gameId, game.gameName)
              ? a.points - b.points || a.playerName.localeCompare(b.playerName)
              : b.points - a.points || a.playerName.localeCompare(b.playerName)
          );
        return {
          gameId: game.gameId,
          gameName: game.gameName,
          unit: gameDefinitionUnitById.get(game.gameId.toLowerCase()) ?? 'points',
          rows,
        };
      })
      .sort((a, b) => a.gameName.localeCompare(b.gameName));
  }, [scores, playerById, gameDefinitionNameById, gameDefinitionUnitById, playerTotals]);

  const groupedGameResultCards = useMemo(
    () => groupItems(gameResultCards, (card) => card.gameId, (card) => card.gameName),
    [gameResultCards]
  );

  const mysteryHighlights = useMemo(() => {
    const winnerByKey = new Map<string, { holeNumber: number; subgameId: string; winnerPlayerId: string }>();
    holeEvents.forEach((event) => {
      const eventType = getString(event.eventType, event.EventType)?.toLowerCase() ?? '';
      if (!eventType.includes('mystery')) return;
      const parsedValue = parseEventValueObject(getString(event.eventValue, event.EventValue));
      if (!parsedValue) return;
      const subgameId = getString(
        parsedValue['subgameId'],
        parsedValue['SubgameId'],
        parsedValue['gameId'],
        parsedValue['GameId']
      );
      const winnerPlayerId = getString(
        parsedValue['winnerPlayerId'],
        parsedValue['WinnerPlayerId'],
        parsedValue['playerId'],
        parsedValue['PlayerId']
      );
      const holeNumber =
        getNumber(parsedValue['holeNumber'], parsedValue['HoleNumber'], event.holeNumber, event.HoleNumber) ?? 0;
      if (!subgameId || !winnerPlayerId || holeNumber <= 0) return;
      winnerByKey.set(`${holeNumber}:${subgameId.toLowerCase()}`, { holeNumber, subgameId, winnerPlayerId });
    });

    const toHighlight = (holeNumber: number, subgameId: string, winnerPlayerId: string): MysteryHighlight => {
      const winnerName = playerById.get(winnerPlayerId)?.name ?? t('summary.unknownWinner');
      const subgameName = getSubgameDisplayName(
        {
          id: subgameId,
          name: gameDefinitionNameById.get(subgameId.toLowerCase()),
        },
        t('summary.mysteryPrizeFallback')
      );
      return {
        holeNumber,
        mysteryPrize: subgameName,
        subgameName,
        winnerName,
      };
    };

    const predefinedKeys = new Set<string>();
    const highlights = roundSubgames
      .map((entry): MysteryHighlight | null => {
        const playerId = getString(entry.playerId, entry.PlayerId);
        const holeNumber = getNumber(entry.holeNumber, entry.HoleNumber) ?? 0;
        const subgameId = getString(entry.subgameId, entry.SubgameId);
        if (!playerId || !subgameId || holeNumber <= 0) return null;
        const key = `${holeNumber}:${subgameId.toLowerCase()}`;
        predefinedKeys.add(key);
        const winnerPlayerId = winnerByKey.get(key)?.winnerPlayerId ?? playerId;
        return toHighlight(holeNumber, subgameId, winnerPlayerId);
      })
      .filter((entry): entry is MysteryHighlight => entry !== null);

    winnerByKey.forEach((winner, key) => {
      if (predefinedKeys.has(key)) return;
      highlights.push(toHighlight(winner.holeNumber, winner.subgameId, winner.winnerPlayerId));
    });

    return highlights.sort(
      (a, b) => a.holeNumber - b.holeNumber || a.mysteryPrize.localeCompare(b.mysteryPrize)
    );
  }, [roundSubgames, holeEvents, playerById, gameDefinitionNameById]);

  const parOrBetterByPlayer = useMemo(() => {
    const byPlayer = new Map<string, Map<'eagle' | 'birdie' | 'par', { resultLabel: string; holeNumbers: number[] }>>();
    holeEntries.forEach((entry) => {
      const playerId = getString(entry.playerId, entry.PlayerId);
      const holeNumber = getNumber(entry.holeNumber, entry.HoleNumber, entry.hole, entry.Hole) ?? 0;
      const par = getNumber(entry.par, entry.Par);
      const gross = getNumber(entry.grossStroke, entry.GrossStroke, entry.grossStrokes, entry.GrossStrokes);
      const pickup = getBoolean(entry.pickup, entry.Pickup) ?? false;
      if (!playerId || holeNumber <= 0 || par == null || gross == null || pickup) return;
      if (gross <= par) {
        const result = getParResult(gross, par);
        if (result.resultType !== 'par' && result.resultType !== 'birdie' && result.resultType !== 'eagle') {
          return;
        }

        const current = byPlayer.get(playerId) ?? new Map<'eagle' | 'birdie' | 'par', { resultLabel: string; holeNumbers: number[] }>();
        const currentGroup = current.get(result.resultType) ?? { resultLabel: result.resultLabel, holeNumbers: [] };
        currentGroup.holeNumbers.push(holeNumber);
        current.set(result.resultType, currentGroup);
        byPlayer.set(playerId, current);
      }
    });

    const resultOrder: Array<'eagle' | 'birdie' | 'par'> = ['eagle', 'birdie', 'par'];

    return Array.from(byPlayer.entries())
      .map(([playerId, groups]): ParOrBetterRow => ({
        playerId,
        playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
        resultGroups: resultOrder
          .map((resultType) => {
            const group = groups.get(resultType);
            if (!group || group.holeNumbers.length === 0) {
              return null;
            }

            return {
              resultLabel: group.resultLabel,
              resultType,
              holeNumbers: group.holeNumbers.sort((a, b) => a - b),
            };
          })
          .filter((group): group is ParOrBetterRow['resultGroups'][number] => group !== null),
      }))
      .sort(
        (a, b) =>
          b.resultGroups.reduce((sum, group) => sum + group.holeNumbers.length, 0) -
            a.resultGroups.reduce((sum, group) => sum + group.holeNumbers.length, 0) ||
          a.playerName.localeCompare(b.playerName)
      );
  }, [holeEntries, playerById]);

  const hazardByPlayer = useMemo(() => {
    const byPlayer = new Map<string, HazardByPlayerRow>();

    const getEventPlayerId = (event: HoleEventEntry): string | undefined => {
      const directPlayerId = getString(event.playerId, event.PlayerId);
      if (directPlayerId) return directPlayerId;
      const parsedValue = parseEventValueObject(getString(event.eventValue, event.EventValue));
      return getString(
        parsedValue?.['playerId'],
        parsedValue?.['PlayerId'],
        parsedValue?.['winnerPlayerId'],
        parsedValue?.['WinnerPlayerId']
      );
    };

    holeEvents.forEach((event) => {
      const playerId = getEventPlayerId(event);
      if (!playerId) return;
      const eventType = getString(event.eventType, event.EventType);
      if (!eventType) return;
      const hazardType = normalizeHazardType(eventType);
      if (!hazardType) return;

      const current =
        byPlayer.get(playerId) ??
        {
          playerId,
          playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
          total: 0,
          water: 0,
          bunker: 0,
          threePutt: 0,
          penalty: 0,
        };
      current[hazardType] += 1;
      current.total += 1;
      byPlayer.set(playerId, current);
    });

    return Array.from(byPlayer.values())
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total || a.playerName.localeCompare(b.playerName));
  }, [holeEvents, playerById]);

  const playerNameById = useMemo(() => {
    const map = new Map<string, string>();
    players.forEach((player) => map.set(player.id, player.name));
    return map;
  }, [players]);

  const meritsByHoleGraphData = useMemo(() => {
    if (!meritsByHole) {
      return null;
    }

    const holes = (meritsByHole.holes ?? meritsByHole.Holes ?? []).filter(
      (hole): hole is number => typeof hole === 'number' && Number.isFinite(hole)
    );
    const series = (meritsByHole.series ?? meritsByHole.Series ?? [])
      .map((entry): PositionByHoleSeriesEntry | null => {
        const playerId = getString(entry.playerId, entry.PlayerId);
        const totals = (entry.totals ?? entry.Totals ?? []).filter(
          (value): value is number => typeof value === 'number' && Number.isFinite(value)
        );
        if (!playerId || totals.length === 0) {
          return null;
        }
        return { playerId, totals };
      })
      .filter((entry): entry is PositionByHoleSeriesEntry => entry !== null)
      .sort(
        (a, b) =>
          (b.totals[b.totals.length - 1] ?? 0) - (a.totals[a.totals.length - 1] ?? 0) ||
          (playerById.get(a.playerId)?.name ?? '').localeCompare(playerById.get(b.playerId)?.name ?? '')
      );

    if (holes.length === 0 || series.length === 0) {
      return null;
    }

    return { holes, series };
  }, [meritsByHole, playerById]);

  const eventTotalsByPlayer = useMemo(() => {
    const map = new Map<string, number>();
    hazardByPlayer.forEach((row) => map.set(row.playerId, row.total));
    return map;
  }, [hazardByPlayer]);

  const getGameIcon = useCallback((gameName: string) => {
    const normalized = normalizeGameKey(gameName);
    const icon = GAME_ICON_MAP[normalized];
    return icon ?? TrophyOnlyImage;
  }, []);

  const selectedRound = useMemo(
    () => rounds.find((candidate) => candidate.id === effectiveRoundId) ?? round,
    [effectiveRoundId, round, rounds]
  );
  const lastPlayedHole = useMemo(() => {
    let maxHole = 0;
    holeEntries.forEach((entry) => {
      const holeNumber = getNumber(entry.holeNumber, entry.HoleNumber, entry.hole, entry.Hole) ?? 0;
      if (holeNumber > maxHole) {
        maxHole = holeNumber;
      }
    });
    return maxHole;
  }, [holeEntries]);
  // Object form rather than a path string: typed routes reject a plain string, and an invite
  // viewer's token has to ride along (nothing about them is persisted, so the URL is the
  // credential). Both extra params are omitted when absent, leaving signed-in users unaffected.
  const doneTarget = useMemo(
    () => {
      if (!effectiveRoundId) {
        return { pathname: '/my-tours' as const, params: {} };
      }

      const fallbackHole = lastPlayedHole > 0 ? lastPlayedHole : 1;
      const holeToOpen = storedHole && storedHole > 0 ? storedHole : fallbackHole;

      return {
        pathname: '/round/[roundId]/hole/[hole]/compact' as const,
        params: {
          roundId: effectiveRoundId,
          hole: String(holeToOpen),
          ...(effectiveTourId ? { tourId: effectiveTourId } : {}),
          ...viewerRouteParams,
        },
      };
    },
    [effectiveRoundId, effectiveTourId, lastPlayedHole, storedHole, viewerRouteParams]
  );
  const isFinishedRound = String(round?.status ?? selectedRound?.status ?? '').trim().toLowerCase() === 'finished';
  const handleReopenRound = useCallback(() => {
    if (!effectiveRoundId || !user?.token || reopening) {
      return;
    }

    setReopening(true);
    setReopenError(null);
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    // 'queued' is not a failure — the frame is flushed when the socket (re)opens, so apply the
    // optimistic status either way; the server's round events correct it if the reopen is refused.
    socket.send({ type: 'round.status.update', roundId: effectiveRoundId, status: 'playing' });

    setRound((current) => (current ? { ...current, status: 'playing' } : current));
    setRoundStatus('playing');
    setReopening(false);
  }, [apiBaseUrl, effectiveRoundId, reopening, setRoundStatus, user?.token]);

  // Shared by the round picker modal and the load-failure state below, so both offer the
  // same list. `replace` is used on the failure path: the round that could not be opened
  // should not stay in the history stack behind the one the player picks instead.
  const renderRoundOption = (candidate: Round, options?: { replace?: boolean }) => {
    const isSelectable = isRoundSelectableByStatus(candidate.status);
    const status = String(candidate.status ?? '-').trim() || '-';
    return (
      <Pressable
        key={candidate.id}
        testID={`summary-round-option-${candidate.id}`}
        style={[
          styles.roundOption,
          candidate.id === effectiveRoundId ? styles.roundOptionActive : null,
          !isSelectable ? { opacity: 0.5 } : null,
        ]}
        onPress={() => {
          if (!isSelectable) {
            return;
          }

          setRoundPickerOpen(false);
          // The object form rather than a template literal: typed routes reject a plain string,
          // and an invite viewer needs their token carried onto the next screen (it is their only
          // credential). Omitted entirely for everyone else, so signed-in users are unaffected.
          const target = {
            pathname: '/round/[roundId]/summary' as const,
            params: {
              roundId: candidate.id,
              tourId: effectiveTourId ?? '',
              ...viewerRouteParams,
            },
          };
          if (options?.replace) {
            router.replace(target);
            return;
          }
          router.push(target);
        }}
        disabled={!isSelectable}
      >
        <Text style={styles.roundOptionTitle}>
          {candidate.name ?? candidate.courseName ?? t('scorecard.selectRoundFallback')}
        </Text>
        <Text style={styles.roundOptionSubtitle}>
          {isSelectable ? status : `${status} (Not started)`}
        </Text>
      </Pressable>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#A1D493" />
      </View>
    );
  }

  // A failed round load used to be a dead end — one line of red text and no way out but the
  // bottom menu, which points back at the same round. The tour's rounds are already loaded
  // here (and readable with guest claim credentials), so offer them: picking another round
  // is what a player actually wants when the one we guessed cannot be opened.
  if (error) {
    return (
      <RNView style={styles.pageBackground}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.container}>
            <Text style={styles.error}>{error}</Text>
            {effectiveTourId ? (
              <View style={styles.sectionCard} testID="summary-error-round-picker">
                <Text style={styles.sectionTitle}>{t('scorecard.selectRoundTitle')}</Text>
                {roundsLoading ? <Text style={styles.emptyText}>{t('scorecard.selectRoundLoading')}</Text> : null}
                {roundsError ? <Text style={styles.error}>{roundsError}</Text> : null}
                {!roundsLoading && !rounds.length ? (
                  <Text style={styles.emptyText}>{t('scorecard.selectRoundEmpty')}</Text>
                ) : null}
                <View style={styles.roundPickerOptions}>
                  {rounds.map((candidate) => renderRoundOption(candidate, { replace: true }))}
                </View>
              </View>
            ) : null}
          </View>
        </ScrollView>
      </RNView>
    );
  }

  return (
    <RNView style={styles.pageBackground}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.container}>
          {/* Resolving a share link needs a bearer token, so it can only fail for a viewer. */}
          {!isInviteViewer ? <ShareLinkButton tourId={effectiveTourId} style={{ marginBottom: 12 }} /> : null}
          <View style={styles.hero}>
            <Text style={styles.title}>{round?.name || t('summary.title')}</Text>
            <Text style={styles.subtitle}>{round?.courseName ?? '-'}</Text>
          </View>

          {isGuestClaimMode ? (
            <View style={styles.sectionCard} testID="summary-claim-signup-card">
              <Text style={styles.sectionTitle}>{t('claimSignup.title')}</Text>
              <Text style={styles.emptyText}>{t('claimSignup.body')}</Text>
              <GoffyButton
                label={t('claimSignup.cta')}
                onPress={() => router.push('/register')}
                testID="summary-claim-signup-cta"
              />
            </View>
          ) : null}

          {effectiveTourId ? (
            <View style={styles.sectionCard}>
              <Text style={styles.sectionTitle}>{t('scorecard.selectRoundTitle')}</Text>
              <Pressable style={styles.roundPicker} onPress={() => setRoundPickerOpen(true)}>
                <Text style={styles.roundPickerText} numberOfLines={1}>
                  {selectedRound?.name ?? selectedRound?.courseName ?? t('scorecard.selectRoundFallback')}
                </Text>
                <Text style={styles.roundPickerHint}>{t('scorecard.selectRound')}</Text>
              </Pressable>
              {roundsLoading ? <Text style={styles.emptyText}>{t('scorecard.selectRoundLoading')}</Text> : null}
              {roundsError ? <Text style={styles.error}>{roundsError}</Text> : null}
              {!roundsLoading && !rounds.length ? <Text style={styles.emptyText}>{t('scorecard.selectRoundEmpty')}</Text> : null}
            </View>
          ) : null}

          {/* Locking freeze holes is a write, and /round/{id}/freeze has no anonymous path. */}
          {canWrite && isFinishedRound && tourHasFreeze && effectiveTourId && effectiveRoundId ? (
            <View testID="summary-freeze-card">
              <FreezeStandingsCard
                section={scopedFreezeSection}
                playerNameLookup={freezeEclecticPlayerNameLookup}
                showDisabledCtaWhenClosed
                expandAllByDefault
                onOpenLockScreen={() =>
                  router.push(
                    `/round/${effectiveRoundId}/freeze?tourId=${encodeURIComponent(effectiveTourId)}`,
                  )
                }
              />
            </View>
          ) : null}

          {/* Eclectic is read-only (no locking), so it's shown as soon as the
              tour's last round is underway — no write-access gate needed. */}
          {tourHasEclectic && effectiveTourId && scopedEclecticSection?.isFinalRound ? (
            <View testID="summary-eclectic-card">
              <EclecticStandingsCard section={scopedEclecticSection} playerNameLookup={freezeEclecticPlayerNameLookup} />
            </View>
          ) : null}

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.overallTitle')}</Text>
            <View style={styles.scoreboardHeader}>
              <Text style={[styles.headerText, styles.headerPlayer]}>{t('summary.playerHeader')}</Text>
              <Text style={[styles.headerText, styles.headerValue]}>{t('summary.gameHeader')}</Text>
            </View>
            {playerScores.map((player, index) => {
              const eventCount = eventTotalsByPlayer.get(player.id) ?? 0;
              return (
                <View key={player.id} style={[styles.playerRow, index === 0 && styles.leaderRow, styles.playerRowCard]}>
                  <View style={styles.playerNameBlock}>
                    <Text style={styles.playerName}>{player.name}</Text>
                    {eventCount > 0 ? (
                      <Text style={styles.playerEvents}>{t('summary.eventsCount', { count: eventCount })}</Text>
                    ) : null}
                  </View>
                  <Text style={styles.playerScore}>{player.gamePoints}</Text>
                </View>
              );
            })}
          </View>

          {trackedShotsByPlayer.length > 0 ? (
            <View style={styles.sectionCard} testID="summary-shot-tracking">
              <Text style={styles.sectionTitle}>{t('summary.shotTrackingTitle')}</Text>
              {trackedShotsByPlayer.map((playerGroup) => (
                <View key={playerGroup.playerId} style={styles.playerNameBlock}>
                  <Text style={styles.playerName}>
                    {playerById.get(playerGroup.playerId)?.name ?? t('summary.unknownPlayer')}
                  </Text>
                  {playerGroup.holes.map((hole) => {
                    const distances = hole.shots
                      .filter((shot) => shot.distanceMeters !== null)
                      .map((shot) => formatHoleDistance(shot.distanceMeters as number, distanceUnit));
                    const parts = [] as string[];
                    if (hole.shots.length > 0) {
                      parts.push(
                        t('summary.shotTrackingShots', { count: hole.shots.length }) +
                          (distances.length > 0 ? ` (${distances.join(', ')})` : '')
                      );
                    }
                    if (hole.putts > 0) {
                      parts.push(t('summary.shotTrackingPutts', { putts: hole.putts }));
                    }
                    return (
                      <Text key={hole.holeNumber} style={styles.playerEvents}>
                        {t('summary.shotTrackingHole', { hole: hole.holeNumber })} · {parts.join(' · ')}
                      </Text>
                    );
                  })}
                </View>
              ))}
            </View>
          ) : null}

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.gamesTitle')}</Text>
            {gameResultCards.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.gamesEmpty')}</Text>
            ) : (
              groupedGameResultCards.map((group) => (
                <View key={group.groupId} style={styles.gameGroup}>
                  <Text style={styles.groupHeader}>{group.title}</Text>
                  {group.items.map((game) => (
                    <View key={`${game.gameId}:${game.gameName}`} style={styles.gameCard}>
                      <View style={styles.gameHeaderRow}>
                        <Image source={getGameIcon(game.gameName)} style={styles.gameIcon} resizeMode="cover" />
                        <Text style={styles.gameTitle}>{game.gameName}</Text>
                      </View>
                      {game.rows.map((row) => (
                        <View key={`${game.gameId}:${row.playerId}`} style={styles.gameRow}>
                          <Text style={styles.gameRowPlayer}>{row.playerName}</Text>
                          <View style={styles.gameRowValues}>
                            <Text style={styles.gameRowPoints}>
                              {(() => {
                                const value = game.unit === 'pieces' ? Math.abs(row.points) : row.points;
                                switch (game.unit) {
                                  case 'strokes':
                                    return t('summary.strokesValue', { strokes: value });
                                  case 'pieces':
                                    return t('summary.piecesValue', { pieces: value });
                                  case 'holes':
                                    return t('summary.holesValue', { holes: value });
                                  case 'skins':
                                    return t('summary.skinsValue', { skins: value });
                                  default:
                                    return t('summary.pointsValue', { points: value });
                                }
                              })()}
                            </Text>
                            <Text style={styles.gameRowMerits}>{t('summary.meritsValue', { merits: row.merits })}</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.hazardsTitle')}</Text>
            {hazardByPlayer.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.hazardsEmpty')}</Text>
            ) : (
              hazardByPlayer.map((row) => (
                <View key={row.playerId} style={styles.hazardRow}>
                  <View style={styles.hazardHeader}>
                    <Text style={styles.hazardPlayer}>{row.playerName}</Text>
                    <View style={styles.hazardBadge}>
                      <Text style={styles.hazardBadgeText}>{t('summary.hazardTotal', { count: row.total })}</Text>
                    </View>
                  </View>
                  <View style={styles.hazardBadges}>
                    {row.water > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.water} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardWater', { count: row.water })}</Text>
                      </View>
                    ) : null}
                    {row.bunker > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.bunker} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardBunker', { count: row.bunker })}</Text>
                      </View>
                    ) : null}
                    {row.threePutt > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.threePutt} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardThreePutt', { count: row.threePutt })}</Text>
                      </View>
                    ) : null}
                    {row.penalty > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.penalty} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardPenalty', { count: row.penalty })}</Text>
                      </View>
                    ) : null}
                  </View>
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.mysteryTitle')}</Text>
            {mysteryHighlights.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.mysteryEmpty')}</Text>
            ) : (
              mysteryHighlights.map((highlight, index) => (
                <View key={`${highlight.holeNumber}:${highlight.mysteryPrize}:${index}`} style={styles.mysteryRow}>
                  <Image source={MysterySelectImage} style={styles.mysteryIcon} resizeMode="cover" />
                  <View style={styles.mysteryTextBlock}>
                    <Text style={styles.mysteryLine}>
                      {t('summary.mysteryHolePrize', {
                        hole: highlight.holeNumber,
                        prize: highlight.mysteryPrize,
                      })}
                    </Text>
                    <Text style={styles.mysterySubgame}>{t('summary.mysterySubgame', { subgame: highlight.subgameName })}</Text>
                    <Text style={styles.mysteryWinner}>{t('summary.mysteryWinner', { winner: highlight.winnerName })}</Text>
                  </View>
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.parBetterTitle')}</Text>
            {parOrBetterByPlayer.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.parBetterEmpty')}</Text>
            ) : (
              parOrBetterByPlayer.map((row) => (
                <View key={row.playerId} style={styles.parRow}>
                  <Text style={styles.parRowPlayer}>{row.playerName}</Text>
                  {row.resultGroups.map((group) => (
                    <View key={`${row.playerId}-${group.resultType}`} style={styles.parResultRow}>
                      <Text style={styles.parResultLabel}>
                        {group.resultType === 'birdie' ? `${group.resultLabel} 🐔` : group.resultType === 'eagle' ? `${group.resultLabel} 🦅` : group.resultLabel}
                      </Text>
                      <Text style={styles.parResultHoles}>
                        {group.holeNumbers.map((holeNumber) => `Hole ${holeNumber}`).join(', ')}
                      </Text>
                    </View>
                  ))}
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.positionByHoleTitle')}</Text>
            {meritsByHoleGraphData ? (
              <PositionByHoleGraph
                holes={meritsByHoleGraphData.holes}
                series={meritsByHoleGraphData.series}
                playerNameById={playerNameById}
              />
            ) : (
              <Text style={styles.emptyText}>{t('summary.positionByHoleEmpty')}</Text>
            )}
          </View>

          {/* handleReopenRound already no-ops without a token, so this was a dead button for every
              signed-out visitor. Viewers (and claim guests) simply do not get it. */}
          {canWrite && isFinishedRound ? (
            <>
              <GoffyButton
                label={t('summary.reopenRoundButton')}
                onPress={handleReopenRound}
                tone="dark"
                size="compact"
                disabled={reopening}
                style={styles.reopenButton}
              />
              {reopenError ? <Text style={styles.error}>{reopenError}</Text> : null}
            </>
          ) : null}

          <GoffyButton
            label={t('summary.doneButton')}
            onPress={() => router.push(doneTarget)}
            tone="accent"
            style={styles.doneButtonImageMode}
          />
        </View>
      </ScrollView>
      <Modal visible={roundPickerOpen} transparent animationType="fade" onRequestClose={() => setRoundPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setRoundPickerOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.sectionTitle}>{t('scorecard.selectRoundTitle')}</Text>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.roundPickerOptions}>
                {rounds.map((candidate) => renderRoundOption(candidate))}
              </View>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </RNView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#011806',
  },
  pageBackground: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
  },
  container: {
    flex: 1,
    padding: 18,
    backgroundColor: 'transparent',
    gap: 12,
  },
  hero: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#A1D493',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  reopenButton: {
    marginTop: 10,
    alignSelf: 'center',
  },
  title: {
    fontSize: 30,
    fontWeight: '900',
    color: appColors.text.primary,
    textAlign: 'left',
    fontFamily: 'Sanchez',
  },
  subtitle: {
    fontSize: 14,
    color: appColors.text.primary,
    textAlign: 'left',
    marginTop: 4,
    fontFamily: 'Sanchez',
  },
  sectionCard: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#A1D493',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  sectionTitle: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  scoreboardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(42,119,66,0.3)',
    paddingBottom: 6,
  },
  headerText: {
    color: appColors.text.primary,
    fontWeight: '800',
    fontSize: 13,
    fontFamily: 'Sanchez',
  },
  headerPlayer: {
    flex: 1,
  },
  headerValue: {
    minWidth: 56,
    textAlign: 'right',
  },
  playerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  playerRowCard: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.26)',
    backgroundColor: appColors.background.panelAlt,
  },
  leaderRow: {
    backgroundColor: 'rgba(42,119,66,0.18)',
    borderColor: '#A1D493',
  },
  playerNameBlock: {
    flex: 1,
    gap: 2,
  },
  playerName: {
    color: '#C8C8B0',
    fontSize: 15,
    fontFamily: 'Sanchez',
  },
  playerEvents: {
    color: 'rgba(200,200,176,0.7)',
    fontSize: 12,
    fontFamily: 'Sanchez',
  },
  playerPoints: {
    color: '#C8C8B0',
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'Sanchez',
    minWidth: 56,
    textAlign: 'right',
  },
  playerScore: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '900',
    fontFamily: 'Sanchez',
    minWidth: 56,
    textAlign: 'right',
  },
  gameGroup: {
    gap: 8,
  },
  groupHeader: {
    fontSize: 13,
    fontWeight: '800',
    color: appColors.text.secondary,
    fontFamily: 'Sanchez',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 4,
  },
  gameCard: {
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.26)',
    padding: 10,
    gap: 6,
  },
  gameHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gameIcon: {
    width: 30,
    height: 30,
    borderRadius: 6,
  },
  gameTitle: {
    color: '#C8C8B0',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  gameRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  gameRowPlayer: {
    color: appColors.text.primary,
    fontSize: 14,
    flex: 1,
    fontFamily: 'Sanchez',
  },
  gameRowPoints: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  gameRowValues: {
    alignItems: 'flex-end',
  },
  gameRowMerits: {
    color: appColors.text.muted,
    fontSize: 12,
    fontFamily: 'Sanchez',
  },
  hazardRow: {
    gap: 6,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(45,98,53,0.28)',
  },
  hazardPlayer: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  hazardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  hazardBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  hazardBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    backgroundColor: 'rgba(16,50,20,0.86)',
  },
  hazardBadgeText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  hazardIcon: {
    width: 25,
    height: 25,
    borderRadius: 4,
  },
  mysteryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: appColors.background.panelAlt,
  },
  mysteryIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
  },
  mysteryTextBlock: {
    flex: 1,
    gap: 2,
  },
  mysteryLine: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  mysteryWinner: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  mysterySubgame: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  parRow: {
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: appColors.background.panelAlt,
    gap: 4,
  },
  parRowPlayer: {
    color: '#C8C8B0',
    fontSize: 14,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  parResultRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    flexWrap: 'wrap',
  },
  parResultLabel: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  parResultHoles: {
    color: '#C8C8B0',
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  emptyText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontFamily: 'Sanchez',
  },
  doneButtonImageMode: {
    width: 220,
    height: 62,
    alignSelf: 'center',
    marginTop: 4,
  },
  error: {
    color: '#fca5a5',
    textAlign: 'center',
  },
  roundPicker: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(16,50,20,0.86)',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundPickerText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
    fontFamily: 'Sanchez',
  },
  roundPickerHint: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
    marginLeft: 12,
    fontFamily: 'Sanchez',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#A1D493',
    padding: 12,
    maxHeight: '80%',
    gap: 10,
  },
  roundPickerOptions: {
    gap: 8,
    backgroundColor: 'transparent',
  },
  roundOption: {
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: appColors.background.panelAlt,
  },
  roundOptionActive: {
    borderColor: '#A1D493',
    backgroundColor: 'rgba(42,119,66,0.18)',
  },
  roundOptionTitle: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  roundOptionSubtitle: {
    color: appColors.text.primary,
    fontSize: 12,
    marginTop: 2,
    fontFamily: 'Sanchez',
  },
});
